/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch, AnyAction} from 'redux';

import PhenixWebSocket, {PhenixWebSocketType} from 'services/websocket.provider';
import pcastProtocol from 'services/messaging/pcast.proto.json';
import analyticsProtocol from 'services/messaging/analytics.proto.json';
import {autoAuthenticate} from 'services/auth.service';
import BackendInfoService from 'services/backend-info.service';
import hostService from 'services/host-url.service';
import websocketConnections from 'services/websocket.connections';
import LoggerFactory from 'services/logger/LoggerFactory';
import {
  wsUpdateStatus,
  wsError,
  WS_CONNECT,
  WS_CONNECTED,
  wsStatus,
  WS_STATUS_UPDATE,
  WS_ROLLED_CONNECTIONS_DISCONNECT,
  WS_LOGOUT,
  wsRemoveRolledSocket,
  wsConnected,
  IWSConnectedActions
} from 'store/action/websocket';
import {updateUser} from 'store/action/user';

import {IReduxStore} from 'interfaces/storeProps';

export const socketOnConnectedMiddleware = (
  store: IReduxStore,
  socket: PhenixWebSocket,
  callback: any // eslint-disable-line
): void => {
  store.dispatch(wsUpdateStatus(socket, wsStatus.connected));

  if (callback && !callback.hasBeenExecuted) {
    callback.hasBeenExecuted = true;
    callback(socket);
  }

  const activeSocket = websocketConnections.getActiveSocket();
  let previousSessionId = '';

  if (!activeSocket || !activeSocket.uniqueId) {
    websocketConnections.setActiveSocket(socket);
  } else if (activeSocket.isAuthenticated()) {
    previousSessionId = activeSocket.getSessionId();
  } else if (activeSocket.isDisconnected()) {
    websocketConnections.setActiveSocket(socket);
  }

  autoAuthenticate(socket, previousSessionId).then(({applicationId}) => {
    websocketConnections.setActiveSocket(socket);
    store.dispatch(updateUser(applicationId));
    store.dispatch(wsUpdateStatus(socket, wsStatus.authenticated));
  }).catch(() => {
    return;
  });
};

export const onConnected = (store: IReduxStore, socket: PhenixWebSocket, action: IWSConnectedActions) => (): void => {
  store.dispatch(wsConnected(socket, action.callback));
};

export const onDisconnected = (store: IReduxStore, phenixSocket: PhenixWebSocket) => (): void => {
  store.dispatch(wsUpdateStatus(phenixSocket, wsStatus.disconnected));
};

export const onReconnecting = (store: IReduxStore, phenixSocket: PhenixWebSocket) => (): void => {
  store.dispatch(wsUpdateStatus(phenixSocket, wsStatus.reconnecting));
};

export const onError = (store: IReduxStore) => (error: string | null): void => {
  store.dispatch(wsError(error));
};

export const initiateConnection = (host: string, store: IReduxStore, action: IWSConnectedActions): boolean | null => {
  try {
    if (!host) {
      throw new Error('Invalid host name');
    }

    const logger = LoggerFactory.getLogger('store/middleware/websocket');
    const socket: PhenixWebSocketType = new PhenixWebSocket(host, logger, [pcastProtocol, analyticsProtocol]);

    socket.onEvent('connected', onConnected(store, socket, action));
    socket.onEvent('reconnected', onConnected(store, socket, action));
    socket.onEvent('reconnecting', onReconnecting(store, socket));
    socket.onEvent('disconnected', onDisconnected(store, socket));
    socket.onEvent('error', onError(store));

    return true;
  } catch (error) {
    if (action.callback) {
      action.callback(null);
    }

    return null;
  }
};

const socketMiddleware = () => {
  return (store: IReduxStore) => (next: Dispatch<AnyAction>) => (action): Promise<void> => {
    if (typeof action === 'function') {
      return action(store.dispatch, websocketConnections.getActiveSocket());
    }

    switch (action.type) {
      case WS_CONNECT:
        return BackendInfoService.getWebsocketUrl(hostService.getHostUrl()).then(wsHost => {
          initiateConnection(wsHost, store, action);
        }).catch(() => {
          if (action.callback) {
            action.callback(null);
          }
        });

      case WS_CONNECTED:
        socketOnConnectedMiddleware(store, action.socket, action.callback);

        break;

      case WS_STATUS_UPDATE: {
        const socket = action.socket;

        socket.setStatus(action.status);

        const activeSocket = websocketConnections.getActiveSocket();

        if (!activeSocket.uniqueId || socket.uniqueId === activeSocket.uniqueId) {
          next(action);
        }

        break;
      }

      case WS_ROLLED_CONNECTIONS_DISCONNECT: {
        const rolledSockets = websocketConnections.getRolledSockets();

        rolledSockets.forEach(async(socket: PhenixWebSocket) => {
          if (!socket.hasPendingRequest()) {
            if (await socket.logout()) {
              store.dispatch(wsRemoveRolledSocket(socket));
            }
          }
        });

        break;
      }

      case WS_LOGOUT: {
        const socket = websocketConnections.getActiveSocket();

        socket.logout();
        next(action);

        break;
      }

      default:
        return next(action);
    }
  };
};

export default socketMiddleware();